<template>
  <transition name="fade" mode="out-in">
    <button v-if="!to" :class="buttonClass" :style="style" :disabled="disabled" :type="type" :title="title" @click="handleClick" v-bind="$attrs">
      <slot />
    </button>
    <NuxtLink
      v-else
      :to="to"
      :target="target"
      :class="buttonClass"
      :style="style"
      :disabled="disabled"
      :type="type"
      :title="t('menu.navigateTo', { label: title })"
      @click="handleClick"
      v-bind="$attrs"
    >
      <slot />
    </NuxtLink>
  </transition>
</template>

<script lang="ts" setup>
  const { t } = useI18n();
  const emit = defineEmits<{
    (e: 'click'): void;
  }>();

  const props = withDefaults(
    defineProps<{
      title?: string;
      variant?: 'primary' | 'secondary' | 'accent' | 'accent-inverted' | 'orange';
      primaryBorder?: boolean;
      size?: 'small' | 'medium' | 'large';
      to?: string;
      target?: string;
      type?: 'button' | 'submit' | 'reset';
      disabled?: boolean;
      loading?: boolean;
      class?: string;
      style?: string;
      shadow?: boolean;
    }>(),
    {
      variant: 'primary',
      size: 'medium',
      primaryBorder: false,
      type: 'button',
      disabled: false,
      loading: false,
      shouldFade: true,
      shadow: false,
    }
  );

  const handleClick = () => {
    if (props.disabled) return;
    if (props.loading) return;
    emit('click');
  };

  const buttonClass = computed(() => {
    return `button button--${props.variant} button--${props.size} ${props.class} ${props.shadow ? 'button--shadow' : ''}`;
  });
</script>

<style lang="scss" scoped>
  .button {
    @apply bg-white rounded-sm size-12 p-3;
    @apply flex items-center justify-center border;

    @apply border-accent text-accent hover:bg-accent hover:text-white transition-colors duration-300;

    &--shadow {
      @apply shadow-sm;
    }

    &--primary {
      @apply bg-white text-primary;
      @apply border-primary;

      &:hover {
        @apply bg-primary text-white;
      }

      &:disabled {
        @apply bg-blue-whale-lightest;
        @apply cursor-not-allowed;
        .button__label {
          @apply text-primary;
        }
      }
    }

    &--accent {
      @apply bg-white text-accent;
      @apply border-accent;

      &:hover {
        @apply bg-accent text-white;
      }

      &:disabled {
        @apply bg-blue-whale-lightest;
        @apply cursor-not-allowed;
        .button__label {
          @apply text-accent;
        }
      }
    }

    &--accent-inverted {
      @apply bg-accent text-white;
      @apply border-accent;

      &:hover {
        @apply bg-accent/80 text-white;
      }

      &:disabled {
        @apply bg-blue-whale-lightest;
        @apply cursor-not-allowed;
        .button__label {
          @apply text-white;
        }
      }
    }

    &--orange {
      @apply bg-christine text-white;
      @apply border-christine-dark;

      &:hover {
        @apply bg-christine-dark text-white;
      }
    }

    &:disabled {
      &:hover {
        @apply text-accent;
      }
    }

    &--small {
      @apply size-10 p-2;
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.35s ease-in-out;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
